import { useState, useCallback, useEffect } from 'react';
import { apiClient } from '../api/client';

/**
 * `baseUrl` is usually `settings.ollamaBaseUrl`, which defaults to `null`
 * (SSRF hardening: SettingsContext.jsx no longer hardcodes an Ollama default,
 * so it is only sent to the server once a user actually sets one).
 *
 * This hook used to fall back to a hardcoded local-loopback default here and
 * send *that* to every apiClient call — which always named an explicit host,
 * so the server-side resolution routes/settings.js already has (tenant/user
 * `ollama_url`, then `OLLAMA_DEFAULT_BASE_URL` — itself `OLLAMA_BASE_URL`
 * from the environment, falling back to the same loopback default) never got
 * a chance to run. A Docker self-host's own `OLLAMA_BASE_URL` or a hosted
 * tenant's configured Ollama was unreachable through this panel: always
 * probed the container's own loopback instead, which `NODE_ENV=production`
 * (isLocalInstall) refuses outright. `requestBaseUrl` below is only ever the
 * explicit value the user typed, or `undefined` — never a client-invented
 * default.
 */
export function useOllamaModels(baseUrl) {
  const requestBaseUrl = baseUrl || undefined;
  const [installedModels, setInstalledModels] = useState([]);
  const [availableModels, setAvailableModels] = useState([]);
  const [serverStatus, setServerStatus] = useState({ available: false, checking: true });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pulling, setPulling] = useState(null);
  const [pullProgress, setPullProgress] = useState(null);

  // Check Ollama server status
  const checkStatus = useCallback(async () => {
    setServerStatus(prev => ({ ...prev, checking: true }));
    try {
      const status = await apiClient.getOllamaStatus(requestBaseUrl);
      setServerStatus({ available: status.available, checking: false });
      return status.available;
    } catch (err) {
      setServerStatus({ available: false, checking: false });
      return false;
    }
  }, [requestBaseUrl]);

  // Fetch installed models from Ollama server
  const fetchInstalledModels = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.getOllamaModels(requestBaseUrl);
      setInstalledModels(data.models || []);
    } catch (err) {
      setError(err.message || 'Failed to fetch Ollama models');
      setInstalledModels([]);
    } finally {
      setLoading(false);
    }
  }, [requestBaseUrl]);

  // Fetch list of available models to download
  const fetchAvailableModels = useCallback(async () => {
    try {
      const data = await apiClient.getOllamaAvailable();
      setAvailableModels(data.models || []);
    } catch (err) {
      console.error('Failed to fetch available models:', err);
      setAvailableModels([]);
    }
  }, []);

  // Pull (download) a model
  const pullModel = useCallback(async (modelName) => {
    try {
      setPulling(modelName);
      setPullProgress({ status: 'starting', model: modelName });
      setError(null);

      await apiClient.pullOllamaModel(requestBaseUrl, modelName, (progress) => {
        setPullProgress(progress);
      });

      // Refresh the model list after pulling
      await fetchInstalledModels();
      setPullProgress(null);
    } catch (err) {
      setError(err.message || 'Failed to pull model');
      setPullProgress({ status: 'error', error: err.message });
    } finally {
      setPulling(null);
    }
  }, [requestBaseUrl, fetchInstalledModels]);

  // Delete a model
  const deleteModel = useCallback(async (modelName) => {
    try {
      setError(null);
      await apiClient.deleteOllamaModel(requestBaseUrl, modelName);
      // Refresh the model list after deleting
      await fetchInstalledModels();
    } catch (err) {
      setError(err.message || 'Failed to delete model');
      throw err;
    }
  }, [requestBaseUrl, fetchInstalledModels]);

  // Check if a model is installed
  const isInstalled = useCallback((modelName) => {
    return installedModels.some(m => m.name === modelName || m.name.startsWith(modelName + ':'));
  }, [installedModels]);

  // Initial fetch when requestBaseUrl changes
  useEffect(() => {
    const init = async () => {
      const available = await checkStatus();
      if (available) {
        await fetchInstalledModels();
      }
      await fetchAvailableModels();
    };
    init();
  }, [requestBaseUrl, checkStatus, fetchInstalledModels, fetchAvailableModels]);

  return {
    installedModels,
    availableModels,
    serverStatus,
    loading,
    error,
    pulling,
    pullProgress,
    checkStatus,
    fetchInstalledModels,
    fetchAvailableModels,
    pullModel,
    deleteModel,
    isInstalled,
    refresh: fetchInstalledModels
  };
}
